import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api/client';
import type { VoiceType } from '../types/api';
import VoiceSelector from '../components/VoiceSelector';
import ScriptStyleSelector from '../components/ScriptStyleSelector';
import { useToast } from '../hooks/useToast';
import { validateFileType, validateFileSize } from '../utils/format';

export default function DirectPage() {
  const { showToast } = useToast();

  const [scriptMode, setScriptMode] = useState<'direct' | 'generate'>('direct');
  const [scriptText, setScriptText] = useState('');
  const [scriptTitle, setScriptTitle] = useState('');
  const [generatedScript, setGeneratedScript] = useState('');
  const [extractedTextToken, setExtractedTextToken] = useState<string | null>(null);
  const [documentInfo, setDocumentInfo] = useState<{ name: string; words: number } | null>(null);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [documentProgress, setDocumentProgress] = useState(0);
  const [selectedVoice, setSelectedVoice] = useState<VoiceType | null>(null);
  const [voices, setVoices] = useState<VoiceType[]>([]);
  const [selectedStyle, setSelectedStyle] = useState('');
  const [audioResult, setAudioResult] = useState<{ url: string; filename: string } | null>(null);
  const [synthesizing, setSynthesizing] = useState(false);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [generatingTitle, setGeneratingTitle] = useState(false);
  const [scriptEditorOpen, setScriptEditorOpen] = useState(false);
  const [scriptEditorContent, setScriptEditorContent] = useState('');
  const [scriptEditorMode, setScriptEditorMode] = useState<'direct' | 'generate'>('direct');

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.getVoiceTypes().then((res) => setVoices(res.voice_types || [])).catch(() => showToast('加载音色失败', 'error'));
  }, []);

  const getCurrentScript = useCallback(() => {
    if (scriptMode === 'direct') return scriptText.trim();
    return (generatedScript || '').trim();
  }, [scriptMode, scriptText, generatedScript]);

  const canSynthesize = getCurrentScript().length > 0 && selectedVoice !== null;

  const handleDocumentSelect = async (file: File) => {
    if (!validateFileType(file.name)) {
      showToast('仅支持 PDF、DOCX、TXT 文件', 'error');
      return;
    }
    if (!validateFileSize(file)) {
      showToast('文件大小不能超过 10MB', 'error');
      return;
    }

    setDocumentUploading(true);
    setDocumentProgress(0);
    setExtractedTextToken(null);
    setDocumentInfo(null);
    setGeneratedScript('');

    try {
      const response = await api.uploadDocument(file, (progress) => setDocumentProgress(progress));
      setExtractedTextToken(response.text_token || null);
      setDocumentInfo({
        name: response.filename || file.name,
        words: response.word_count || (response.text ? response.text.length : 0),
      });
      if (!response.text_token) {
        throw new Error('未获取到文本令牌，请重试上传');
      }
      showToast('文献解析完成', 'success');
    } catch (error) {
      setExtractedTextToken(null);
      setDocumentInfo(null);
      showToast(error instanceof Error ? error.message : '上传失败', 'error');
    } finally {
      setDocumentUploading(false);
    }
  };

  const handleGenerateScript = async () => {
    if (!extractedTextToken) {
      showToast('请先上传文献文件', 'warning');
      return;
    }
    setGeneratingScript(true);
    try {
      const response = await api.generateScript({
        text_token: extractedTextToken,
        style: selectedStyle || '科普口播稿',
      });
      if (response?.script) {
        setGeneratedScript(response.script);
        showToast('口播稿已生成', 'success');
        // Auto-generate title
        void generateTitleForScript(response.script);
      }
    } catch {
      showToast('生成脚本失败', 'error');
    } finally {
      setGeneratingScript(false);
    }
  };

  const generateTitleForScript = async (script: string) => {
    const trimmed = script.trim();
    if (!trimmed) return;
    setGeneratingTitle(true);
    try {
      const response = await api.generateTitle({
        script_text: trimmed,
        style: selectedStyle || '科普口播稿',
      });
      if (response?.title) {
        setScriptTitle(response.title);
      }
    } catch {
      // Silent fail for auto-title
    } finally {
      setGeneratingTitle(false);
    }
  };

  const handleSynthesize = async () => {
    const script = getCurrentScript();
    if (!script) {
      showToast('请准备口播稿', 'warning');
      return;
    }
    if (!selectedVoice) {
      showToast('请选择音色', 'warning');
      return;
    }
    setSynthesizing(true);
    try {
      const requestParams: Record<string, unknown> = {
        script,
        voice_type: selectedVoice.voice_type,
        async_mode: false,
      };
      if (scriptTitle.trim()) {
        requestParams.script_title = scriptTitle.trim();
      }
      const response = await api.synthesize(requestParams as any);
      if (response?.audio_url) {
        setAudioResult({ url: response.audio_url, filename: response.filename || 'audio.mp3' });
        showToast('语音合成成功', 'success');
      } else {
        throw new Error('合成响应无效');
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : '语音合成失败', 'error');
    } finally {
      setSynthesizing(false);
    }
  };

  const handleReset = () => {
    setSelectedVoice(null);
    setGeneratedScript('');
    setExtractedTextToken(null);
    setDocumentInfo(null);
    setDocumentUploading(false);
    setAudioResult(null);
    setScriptMode('direct');
    setScriptText('');
    setScriptTitle('');
    setSelectedStyle('');
  };

  const handleOpenScriptEditor = (mode?: 'direct' | 'generate') => {
    const targetMode = mode || (scriptMode === 'generate' ? 'generate' : 'direct');
    let content = '';
    if (targetMode === 'generate') {
      content = generatedScript || '';
      if (!content.trim()) {
        showToast('暂无可编辑的口播稿，请先生成内容', 'warning');
        return;
      }
    } else {
      content = scriptText;
    }
    setScriptEditorMode(targetMode);
    setScriptEditorContent(content);
    setScriptEditorOpen(true);
  };

  const handleSaveScriptEditor = () => {
    if (scriptEditorMode === 'generate') {
      setGeneratedScript(scriptEditorContent);
    } else {
      setScriptText(scriptEditorContent);
    }
    setAudioResult(null);
    setScriptEditorOpen(false);
    showToast('口播稿已更新', 'success');
  };

  const handleTitleGenerate = async () => {
    const script = getCurrentScript();
    if (!script) {
      showToast('请先准备口播稿', 'warning');
      return;
    }
    setGeneratingTitle(true);
    try {
      const response = await api.generateTitle({
        script_text: script,
        style: selectedStyle || '科普口播稿',
      });
      if (response?.title) {
        setScriptTitle(response.title);
      }
    } catch {
      showToast('生成标题失败', 'error');
    } finally {
      setGeneratingTitle(false);
    }
  };

  return (
    <div className="page-container direct-page">
      <div className="row">
        <div className="col-12">
          <div className="glass-card hero-card d-flex justify-content-between align-items-center">
            <div>
              <div className="hero-eyebrow text-fg-muted mb-1">直接执行</div>
              <h2 className="mb-2">即时上传 · 直接生成</h2>
              <p className="text-fg-muted mb-0">上传文献或粘贴文稿，快速产出口播稿并立即生成音频</p>
            </div>
          </div>
        </div>
      </div>

      <div className="row step-flow">
        <div className="col-12">
          <div className="glass-card mb-4 step-card">
            <div className="section-header mb-4">
              <div className="section-step-label">产出口播稿脚本</div>
              <p className="section-tip mb-0 text-fg-muted">选择生成方式，并编辑口播稿</p>
            </div>

            <div className="workflow-selector">
              <div
                className={`workflow-option ${scriptMode === 'generate' ? 'active' : ''}`}
                onClick={() => setScriptMode('generate')}
              >
                <div className="option-icon">🤖</div>
                <div className="option-content">
                  <div className="option-title">智能生成</div>
                  <div className="option-desc">上传学术文献，AI自动生成口播稿</div>
                </div>
                <div className="option-arrow">›</div>
              </div>
              <div
                className={`workflow-option ${scriptMode === 'direct' ? 'active' : ''}`}
                onClick={() => setScriptMode('direct')}
              >
                <div className="option-icon">✍️</div>
                <div className="option-content">
                  <div className="option-title">直接输入</div>
                  <div className="option-desc">手动输入或粘贴口播稿内容</div>
                </div>
                <div className="option-arrow">›</div>
              </div>
            </div>

            {scriptMode === 'generate' && (
              <div className="mt-3">
                <div
                  className="upload-zone document-upload-zone"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                  onDragLeave={(e) => e.currentTarget.classList.remove('dragover')}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('dragover');
                    if (e.dataTransfer.files?.[0]) handleDocumentSelect(e.dataTransfer.files[0]);
                  }}
                >
                  <div className="placeholder-text">
                    <strong id="documentUploadText">
                      {documentInfo ? documentInfo.name : '点击选择文献文件'}
                    </strong>
                    <span>支持 PDF / DOCX / TXT</span>
                    {documentInfo && (
                      <span className="file-meta" style={{ display: 'inline' }}>
                        {' '}{documentInfo.words} 字
                      </span>
                    )}
                  </div>
                  <button
                    className="btn btn-outline-light btn-sm mt-3"
                    type="button"
                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  >
                    选择文件
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.txt"
                    hidden
                    onChange={(e) => {
                      if (e.target.files?.[0]) handleDocumentSelect(e.target.files[0]);
                      e.target.value = '';
                    }}
                  />
                </div>

                {documentUploading && (
                  <div className="upload-progress mt-3">
                    <div className="progress">
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${documentProgress}%` }}
                      />
                    </div>
                    <p className="text-muted small mt-2">上传中...</p>
                  </div>
                )}

                {extractedTextToken && (
                  <div className="mt-3">
                    <label className="form-label" htmlFor="scriptStyleSelect">口播稿类型</label>
                    <ScriptStyleSelector value={selectedStyle} onChange={setSelectedStyle} />
                  </div>
                )}

                <button
                  type="button"
                  className="btn btn-primary w-100 mt-3"
                  disabled={!extractedTextToken || documentUploading || generatingScript}
                  onClick={handleGenerateScript}
                >
                  {generatingScript ? (
                    <><i className="fas fa-spinner fa-spin me-2"></i>AI生成中...</>
                  ) : (
                    <><i className="fas fa-magic me-2"></i>AI生成口播稿</>
                  )}
                </button>

                {generatedScript && (
                  <div className="mt-3">
                    <div className="generated-script-header d-flex justify-content-between align-items-center">
                      <label htmlFor="generatedScript" className="form-label mb-0">生成的口播稿</label>
                    </div>
                    <textarea className="form-control" rows={8} value={generatedScript} readOnly />
                    <div className="form-text">{generatedScript.length} 字</div>
                  </div>
                )}
              </div>
            )}

            <div className="title-input-card mb-3 mt-3">
              <div className="title-input-header mb-2 d-flex justify-content-between">
                <div>
                  <div className="fw-bold">标题</div>
                  <div className="text-fg-muted small">可选：用于任务管理展示</div>
                </div>
                <div className="hint-length text-fg-muted">
                  {scriptTitle.length > 0 ? `${scriptTitle.length} 字` : ''}
                </div>
              </div>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="请输入或生成标题"
                  maxLength={100}
                  value={scriptTitle}
                  onChange={(e) => setScriptTitle(e.target.value)}
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  disabled={generatingTitle || !getCurrentScript()}
                  onClick={handleTitleGenerate}
                >
                  {generatingTitle ? (
                    <><i className="fas fa-spinner fa-spin me-2"></i>生成中</>
                  ) : (
                    <><i className="fas fa-sync me-2"></i>更新标题</>
                  )}
                </button>
              </div>
              {generatingTitle && (
                <div className="form-text">
                  <i className="fas fa-spinner fa-spin me-1"></i>正在生成标题...
                </div>
              )}
            </div>

            {scriptMode === 'direct' && (
              <div className="mt-3">
                <label htmlFor="scriptText" className="form-label">口播稿内容</label>
                <textarea
                  className="form-control"
                  id="scriptText"
                  rows={10}
                  placeholder="将口播稿粘贴到这里..."
                  maxLength={5000}
                  value={scriptText}
                  onChange={(e) => { setScriptText(e.target.value); setAudioResult(null); }}
                />
                <div className="form-text">{scriptText.length} 字</div>
              </div>
            )}

            <div className="script-editor-toolbar mt-3 d-flex flex-wrap gap-2 justify-content-end">
              <button
                className="btn btn-outline-light btn-sm"
                type="button"
                disabled={!getCurrentScript()}
                onClick={() => handleTitleGenerate()}
              >
                <i className="fas fa-sync me-2"></i>更新标题
              </button>
              <button
                className="btn btn-outline-light btn-sm"
                type="button"
                onClick={() => handleOpenScriptEditor()}
              >
                <i className="fas fa-expand me-2"></i>打开编辑器
              </button>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="glass-card mb-4 step-card">
            <div className="voice-section-header">
              <div>
                <div className="section-step-label">选择配音音色</div>
                <p className="text-fg-muted mb-0">可使用不同年龄、性别、声学特征的说话人</p>
              </div>
            </div>

            <VoiceSelector
              voices={voices}
              selectedVoice={selectedVoice}
              onChange={setSelectedVoice}
            />
          </div>

          <div className="glass-card">
            <h4 className="mb-3">语音合成</h4>
            <div className="d-grid gap-2">
              <button
                className="btn btn-success btn-lg"
                disabled={!canSynthesize || synthesizing}
                onClick={handleSynthesize}
              >
                {synthesizing ? (
                  <><i className="fas fa-spinner fa-spin me-2"></i>合成中...</>
                ) : (
                  <><i className="fas fa-play me-2"></i>开始合成</>
                )}
              </button>
              <button className="btn btn-secondary" onClick={handleReset}>
                <i className="fas fa-redo me-2"></i>重置
              </button>
            </div>
            {audioResult && (
              <div className="mt-4">
                <div className="card bg-success bg-opacity-10">
                  <div className="card-body">
                    <h5 className="card-title">
                      <i className="fas fa-check-circle text-success me-2"></i>语音合成成功
                    </h5>
                    <audio className="w-100" controls src={audioResult.url} />
                    <div className="mt-3">
                      <a href={audioResult.url} className="btn btn-outline-primary btn-sm" download={audioResult.filename}>
                        <i className="fas fa-download me-2"></i>下载音频
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {scriptEditorOpen && (
        <div className="script-editor-modal show" onClick={(e) => { if (e.target === e.currentTarget) setScriptEditorOpen(false); }}>
          <div className="script-editor-modal-dialog">
            <div className="script-editor-modal-header">
              <h5 className="mb-0">编辑口播稿</h5>
              <button className="script-editor-modal-close" type="button" onClick={() => setScriptEditorOpen(false)}>✕</button>
            </div>
            <div className="script-editor-modal-body">
              <textarea
                className="form-control"
                rows={12}
                maxLength={5000}
                placeholder="请输入口播稿内容..."
                value={scriptEditorContent}
                onChange={(e) => setScriptEditorContent(e.target.value)}
              />
              <div className="script-editor-meta d-flex justify-content-between align-items-center mt-3">
                <span>{scriptEditorContent.length} 字</span>
              </div>
            </div>
            <div className="script-editor-modal-footer justify-content-end gap-2">
              <button className="btn btn-outline-secondary" type="button" onClick={() => setScriptEditorOpen(false)}>取消</button>
              <button className="btn btn-primary" type="button" onClick={handleSaveScriptEditor}>保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}